import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CONSTANTS } from './data';
import { ASSETS } from './assetManifest';
import { ChevronRight, ArrowRight, Trophy, Code, Clock, Users, Check, Star, GitBranch, Zap, X, User } from 'lucide-react';

function LandingPage({ redirectToTypeform }) {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [activeTab, setActiveTab] = useState('competitive');
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState('');
  const [experience, setExperience] = useState('beginner');
  const [solutionText, setSolutionText] = useState('');
  
  // Timer state
  const [timer, setTimer] = useState({ minutes: 15, seconds: 0 });
  const [isTimerRunning, setIsTimerRunning] = useState(true);
  
  // Get current year for copyright
  const currentYear = new Date().getFullYear();

  const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateEmail(email)) {
      setIsValid(true);
      // Instead of showing the modal, directly redirect to Typeform
      redirectToTypeform(email);
    } else {
      setIsValid(false);
    }
  };

  const handleModalSubmit = (e) => {
    e.preventDefault();
    
    if (validateEmail(email)) {
      // Close the modal
      setShowModal(false);
      
      // Redirect to Typeform
      redirectToTypeform(email);
    } else {
      setIsValid(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleChallengeSubmit = () => {
    setShowModal(true);
  };

  const toggleTimer = () => {
    setIsTimerRunning(prev => !prev);
  };

  // Timer countdown effect
  useEffect(() => {
    let interval;
    
    if (isTimerRunning) {
      interval = setInterval(() => {
        setTimer(prevTimer => {
          // If timer reaches 0:00, stop the timer
          if (prevTimer.minutes === 0 && prevTimer.seconds === 0) {
            clearInterval(interval);
            setIsTimerRunning(false);
            return prevTimer;
          }
          
          // Calculate the new timer value
          let newSeconds = prevTimer.seconds - 1;
          let newMinutes = prevTimer.minutes;
          
          if (newSeconds < 0) {
            newSeconds = 59;
            newMinutes -= 1;
          }
          
          return { minutes: newMinutes, seconds: newSeconds };
        });
      }, 1000);
    }
    
    return () => clearInterval(interval);
  }, [isTimerRunning]);

  useEffect(() => {
    if (email) {
      setIsValid(validateEmail(email));
    }
  }, [email]);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showModal]);

  // Format timer to display as MM:SS
  const formatTime = (minutes, seconds) => {
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-gradient-to-b from-gray-900 to-gray-800 min-h-screen text-white relative overflow-hidden">
      {/* Background Patterns */}
      <div className="absolute top-0 left-0 right-0 h-screen overflow-hidden opacity-10 pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-purple-500 rounded-full filter blur-3xl"></div>
        <div className="absolute top-40 right-20 w-96 h-96 bg-pink-600 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-10 left-1/4 w-80 h-80 bg-blue-600 rounded-full filter blur-3xl"></div>
      </div>

      {/* Header */}
      <header className="relative z-10">
        <div className="container mx-auto px-4 py-6 flex justify-between items-center">
          <div className="flex items-center">
            <motion.div
              className="ml-3 font-bold text-xl"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="text-white">{CONSTANTS.SITE_NAME.split(' ')[0]} </span>
              <span className="text-white">{CONSTANTS.SITE_NAME.split(' ')[1]} </span>
              <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">{CONSTANTS.SITE_NAME.split(' ')[2]}</span>
            </motion.div>
          </div>
          <motion.nav
            className="hidden md:flex space-x-8 items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {CONSTANTS.NAVIGATION.map((item, index) => (
              <a 
                key={index}
                href={item.url} 
                className="text-gray-300 hover:text-white transition-colors duration-300"
              >
                {item.name}
              </a>
            ))}
            <a 
              href="#signup" 
              className="px-5 py-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-600 text-white font-medium hover:from-purple-600 hover:to-pink-700 transition-all duration-300 flex items-center"
            >
              Join Now <ChevronRight className="ml-1 w-4 h-4" />
            </a>
          </motion.nav>
          <motion.div
            className="md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <a 
              href="#signup" 
              className="px-5 py-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-600 text-white font-medium hover:from-purple-600 hover:to-pink-700 transition-all duration-300 flex items-center"
            >
              Join <ChevronRight className="ml-1 w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-16 pb-24 overflow-hidden">
        {/* Diagonal Element */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gray-800/50 transform skew-y-1"></div>
        
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <motion.div 
              className="md:w-1/2 mb-10 md:mb-0 relative z-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="absolute -left-6 -top-6 w-20 h-20 bg-purple-900/20 rounded-full filter blur-xl"></div>
              <div className="inline-block px-4 py-1 bg-purple-900/30 rounded-full text-sm font-medium text-purple-300 mb-4">
                {CONSTANTS.HERO.TAG}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                {CONSTANTS.HERO.TITLE} <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">{CONSTANTS.HERO.HIGHLIGHT}</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 max-w-lg">
                {CONSTANTS.HERO.SUBTITLE}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="#signup" 
                  className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full text-white font-medium hover:from-purple-600 hover:to-pink-700 transition-all duration-300 text-center shadow-lg shadow-purple-500/20"
                >
                  Get Started
                </a>
                <a 
                  href="#about" 
                  className="px-8 py-3 bg-gray-800 border border-gray-600 rounded-full text-white font-medium hover:bg-gray-700 transition-all duration-300 text-center"
                >
                  Learn More
                </a>
              </div>
              
              <div className="mt-12 flex items-center space-x-1">
                <div className="flex -space-x-3">
                  {Array(4).fill(0).map((_, i) => (
                    <img 
                      key={i} 
                      src={ASSETS.avatars[`user${i+1}`].path} 
                      alt={`User ${i+1}`} 
                      className="w-8 h-8 rounded-full border-2 border-gray-800 object-cover" 
                    />
                  ))}
                </div>
                <div className="text-sm ml-4">
                  <span className="text-purple-400 font-bold">+830</span>
                  <span className="text-gray-400 ml-1">prompt engineers waiting</span>
                </div>
              </div>
            </motion.div>
            <motion.div 
              className="md:w-1/2 flex justify-center relative"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <div className="relative w-full max-w-lg">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl blur-lg opacity-75 animate-pulse"></div>
                <div className="relative bg-gray-900 rounded-2xl p-5 shadow-2xl border border-gray-800">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">Challenge #13</div>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div 
                      className="text-xs text-gray-400 bg-gray-800 px-2 py-1 rounded flex items-center cursor-pointer"
                      onClick={toggleTimer}
                    >
                      <Clock className="w-3 h-3 mr-1" /> 
                      Time: {formatTime(timer.minutes, timer.seconds)}
                      {isTimerRunning ? 
                        <span className="ml-1 h-2 w-2 bg-red-500 rounded-full animate-pulse"></span> : 
                        <span className="ml-1 h-2 w-2 bg-gray-500 rounded-full"></span>
                      }
                    </div>
                    <div className="text-xs text-purple-400 bg-purple-900/30 px-2 py-1 rounded">Difficulty: Expert</div>
                  </div>
                  
                  <div className="bg-gray-800 rounded-lg p-4 mb-4 font-mono text-sm">
                    <div className="text-purple-400 mb-2"># Challenge: Create a GPT prompt that...</div>
                    <div className="text-gray-300">Create a web game that remixes a classic board game as an arcade game.</div>
                  </div>
                  
                  <div className="bg-gray-800/50 rounded-lg p-4 mb-4 font-mono text-sm">
                    <div className="text-pink-400 mb-2"># Your solution:</div>
                    <textarea
                      className="w-full bg-transparent border-l-2 border-gray-700 pl-3 text-gray-400 font-mono text-sm resize-y focus:outline-none focus:border-pink-500 min-h-[80px]"
                      placeholder="Type your solution here..."
                      value={solutionText}
                      onChange={(e) => setSolutionText(e.target.value)}
                    />
                  </div>
                  
                  <div className="flex justify-end items-center">
                    <button 
                      className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-600 text-white text-sm rounded-lg hover:from-purple-600 hover:to-pink-700 transition-colors duration-300"
                      onClick={handleChallengeSubmit}
                    >
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="about" className="py-20 relative">
        <div className="absolute top-0 left-0 right-0 h-16 bg-gray-900/30 transform -skew-y-1"></div>
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="inline-block px-4 py-1 bg-purple-900/30 rounded-full text-sm font-medium text-purple-300 mb-4">
              {CONSTANTS.FEATURES.TAG}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              {CONSTANTS.FEATURES.TITLE}
            </h2>
            <p className="text-gray-300 text-lg">{CONSTANTS.FEATURES.SUBTITLE}</p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {CONSTANTS.FEATURES.ITEMS.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700/50 hover:border-purple-500/50 transition-all duration-300 relative group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-t-xl"></div>
                <div className="w-12 h-12 bg-purple-900/30 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-900/50 transition-colors duration-300">
                  {feature.icon === 'Trophy' && <Trophy className="text-purple-400" />}
                  {feature.icon === 'Code' && <Code className="text-purple-400" />}
                  {feature.icon === 'Clock' && <Clock className="text-purple-400" />}
                </div>
                <h3 className="text-xl font-semibold mb-2 group-hover:text-purple-300 transition-colors duration-300">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Competition Types Section */}
      <section id="competitions" className="py-20 bg-gray-900/30 relative">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-12 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="inline-block px-4 py-1 bg-purple-900/30 rounded-full text-sm font-medium text-purple-300 mb-4">
              {CONSTANTS.COMPETITION_TYPES.TAG}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              {CONSTANTS.COMPETITION_TYPES.TITLE}
            </h2>
            <p className="text-gray-300 text-lg">{CONSTANTS.COMPETITION_TYPES.SUBTITLE}</p>
          </motion.div>
          
          <div className="flex flex-wrap justify-center mb-8">
            <div className="inline-flex rounded-full bg-gray-800 p-1 mb-6">
              {CONSTANTS.COMPETITION_TYPES.TABS.map((tab, index) => (
                <button
                  key={index}
                  className={`px-6 py-2 rounded-full text-sm font-medium ${activeTab === tab.id ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white' : 'text-gray-300 hover:text-white'}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.name}
                </button>
              ))}
            </div>
          </div>
          
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700/50">
            {CONSTANTS.COMPETITION_TYPES.CONTENT.map((content, index) => (
              <div key={index} className={activeTab === content.id ? 'block' : 'hidden'}>
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="md:w-1/2">
                    <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                      {content.title}
                    </h3>
                    <p className="text-gray-300 mb-4">
                      {content.description}
                    </p>
                    <ul className="space-y-3 mb-6">
                      {content.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <div className="flex-shrink-0 w-5 h-5 bg-purple-900/50 rounded-full flex items-center justify-center mt-1 mr-2">
                            <Check className="w-3 h-3 text-purple-400" />
                          </div>
                          <span className="text-gray-300 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="md:w-1/2 relative">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg blur opacity-30"></div>
                    <div className="relative bg-gray-900 rounded-lg p-4 border border-gray-800">
                      <div className="flex justify-between items-center mb-4">
                        <div className="text-sm text-gray-400">{content.example.type}</div>
                        <div className="px-2 py-1 bg-purple-900/30 rounded text-xs text-purple-300">{content.example.difficulty}</div>
                      </div>
                      <div className="font-mono text-sm bg-gray-800 rounded p-3 mb-4">
                        <div className="text-purple-400 mb-2"># Challenge:</div>
                        <div className="text-gray-300">{content.example.challenge}</div>
                      </div>
                      <div className="bg-gray-800/50 p-3 rounded font-mono text-sm">
                        <div className="text-pink-400 mb-2"># Sample winning solution:</div>
                        <div className="text-gray-400 border-l-2 border-gray-700 pl-3 text-xs">
                          {content.example.solution}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 relative overflow-hidden">
        <div className="absolute left-0 right-0 bottom-0 top-96 bg-gradient-to-b from-transparent to-gray-900/30 pointer-events-none"></div>
        
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="inline-block px-4 py-1 bg-purple-900/30 rounded-full text-sm font-medium text-purple-300 mb-4">
              {CONSTANTS.HOW_IT_WORKS.TAG}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              {CONSTANTS.HOW_IT_WORKS.TITLE}
            </h2>
            <p className="text-gray-300 text-lg">{CONSTANTS.HOW_IT_WORKS.SUBTITLE}</p>
          </motion.div>
          
          <div className="max-w-4xl mx-auto relative">
            <div className="absolute top-0 bottom-0 left-10 w-1 bg-gradient-to-b from-purple-500 to-pink-600 md:block hidden"></div>
            
            {CONSTANTS.HOW_IT_WORKS.STEPS.map((step, index) => (
              <motion.div 
                key={index}
                className="flex mb-16 relative"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="hidden md:flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center font-bold text-2xl mr-8 shadow-lg shadow-purple-500/20 relative z-10">
                    {index + 1}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700/50 flex-1">
                  <div className="md:hidden w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center font-bold text-lg mb-4">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-semibold mb-2 bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent">
                    {step.title}
                  </h3>
                  <p className="text-gray-300">{step.description}</p>
                  
                  {step.action && (
                    <a href={step.action.url} className="mt-4 inline-flex items-center text-purple-400 text-sm hover:text-purple-300 transition-colors duration-300">
                      {step.action.label} <ArrowRight className="w-4 h-4 ml-1" />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-gray-900/30 relative">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="inline-block px-4 py-1 bg-purple-900/30 rounded-full text-sm font-medium text-purple-300 mb-4">
              {CONSTANTS.TESTIMONIALS.TAG}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              {CONSTANTS.TESTIMONIALS.TITLE}
            </h2>
            <p className="text-gray-300 text-lg">{CONSTANTS.TESTIMONIALS.SUBTITLE}</p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {CONSTANTS.TESTIMONIALS.ITEMS.map((testimonial, index) => (
              <motion.div 
                key={index}
                className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700/50 relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="absolute top-6 right-6 text-purple-600 opacity-20">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16.25 27.5H7.5V20C7.5 13.75 10 8.75 17.5 7.5V12.5C13.75 13.75 12.5 16.25 12.5 20H16.25V27.5ZM32.5 27.5H23.75V20C23.75 13.75 26.25 8.75 33.75 7.5V12.5C30 13.75 28.75 16.25 28.75 20H32.5V27.5Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="flex items-center mb-6">
                  <img 
                    src={ASSETS.avatars[`user${index+2}`].path} 
                    alt={testimonial.name} 
                    className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-purple-500"
                  />
                  <div>
                    <p className="font-medium text-lg">{testimonial.name}</p>
                    <p className="text-sm text-purple-300">{testimonial.title}</p>
                  </div>
                </div>
                <div className="mb-4 flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current text-yellow-500" fill="currentColor" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4">"{testimonial.quote}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action / Sign Up */}
      <section id="signup" className="py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-purple-500/5 to-transparent pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-gradient-to-t from-pink-600/5 to-transparent pointer-events-none"></div>
        
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-3xl mx-auto bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 md:p-12 border border-gray-700 shadow-xl relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-purple-800 rounded-full opacity-20 blur-3xl"></div>
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-pink-800 rounded-full opacity-20 blur-3xl"></div>
            
            <div className="relative">
              <div className="inline-block px-4 py-1 bg-purple-900/30 rounded-full text-sm font-medium text-purple-300 mb-4">
                {CONSTANTS.SIGNUP.TAG}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                {CONSTANTS.SIGNUP.TITLE}
              </h2>
              <p className="text-gray-300 text-lg mb-8">{CONSTANTS.SIGNUP.SUBTITLE}</p>
              
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="max-w-md mx-auto">
                  <div className="mb-4">
                    <div className="relative">
                      <input
                        type="email"
                        className={`w-full px-4 py-3 bg-gray-800 border ${isValid ? 'border-gray-600 focus:border-purple-500' : 'border-red-500'} rounded-lg focus:outline-none text-white shadow-inner`}
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                      {!isValid && (
                        <p className="text-red-500 text-sm mt-1">Please enter a valid email address</p>
                      )}
                    </div>
                  </div>
                  <motion.button
                    type="submit"
                    className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg text-white font-medium hover:from-purple-600 hover:to-pink-700 transition-all duration-300 flex items-center justify-center shadow-lg shadow-purple-500/20"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onHoverStart={() => setIsHovered(true)}
                    onHoverEnd={() => setIsHovered(false)}
                  >
                    Join Today
                    <motion.span
                      animate={{ x: isHovered ? 5 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </motion.span>
                  </motion.button>
                  <p className="text-gray-400 text-sm text-center mt-4">
                    We respect your privacy. Unsubscribe at any time.
                  </p>
                  
                  {/* Add referral link */}
                  <div className="text-center mt-8">
                    <p className="text-gray-400 text-sm">
                      Want to invite friends?{' '}
                      <a href="/referral" className="text-purple-400 hover:text-purple-300 transition-colors duration-300">
                        Create a referral link
                      </a>
                    </p>
                  </div>
                </form>
              ) : (
                <motion.div 
                  className="text-center py-8 max-w-md mx-auto"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="text-green-500 w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2 bg-gradient-to-r from-green-400 to-teal-500 bg-clip-text text-transparent">
                    Thank You!
                  </h3>
                  <p className="text-gray-300">You're in! We look forward to seeing you on the platform.</p>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center mb-4">
                <span className="font-bold text-lg">
                  <span className="text-white">MLP</span>
                </span>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                The premier competitive platform for prompt engineers. Join challenges, showcase your skills, and earn recognition.
              </p>
              <div className="flex space-x-4">
                {CONSTANTS.FOOTER.SOCIAL.map((social, index) => (
                  <a 
                    key={index} 
                    href={social.url} 
                    className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-purple-600 hover:text-white transition-colors duration-300"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {social.icon === 'Twitter' && 
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
                      </svg>
                    }
                    {social.icon === 'GitHub' && 
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
                      </svg>
                    }
                    {social.icon === 'Discord' && 
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3847-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914a.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
                      </svg>
                    }
                  </a>
                ))}
              </div>
            </div>
            
            {CONSTANTS.FOOTER.COLUMNS.map((column, index) => (
              <div key={index}>
                <h3 className="font-semibold mb-4 text-white">{column.title}</h3>
                <ul className="space-y-2">
                  {column.links.map((link, idx) => (
                    <li key={idx}>
                      <a 
                        href={link.url} 
                        className="text-gray-400 hover:text-purple-400 transition-colors duration-300 text-sm"
                        target={link.url.startsWith('http') || link.url.startsWith('mailto') ? "_blank" : ""}
                        rel={link.url.startsWith('http') ? "noopener noreferrer" : ""}
                      >
                        {link.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm mb-4 md:mb-0">
              © {currentYear} Major League Prompting. All rights reserved.
            </p>
            <div className="flex space-x-6">
              {CONSTANTS.FOOTER.LEGAL.map((link, index) => (
                <a 
                  key={index} 
                  href={link.url} 
                  className="text-gray-500 hover:text-white transition-colors duration-300 text-sm"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>

      {/* Sign Up Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 md:p-8 border border-gray-700/50 w-full max-w-md shadow-2xl"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Account Creation Header */}
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                  {CONSTANTS.REGISTRATION_MODAL.TITLE}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Account Icon and Step Indicator */}
              <div className="flex flex-col items-center mb-6">
                <div className="w-16 h-16 bg-purple-900/30 rounded-full flex items-center justify-center mb-3">
                  <User className="w-8 h-8 text-purple-400" />
                </div>
                <div className="text-center">
                  <p className="text-white font-medium">{CONSTANTS.REGISTRATION_MODAL.SUBTITLE}</p>
                  <p className="text-gray-400 text-sm mt-1">{CONSTANTS.REGISTRATION_MODAL.DESCRIPTION}</p>
                </div>
                <div className="flex items-center mt-4 space-x-2">
                  <div className="h-1 w-8 bg-purple-500 rounded-full"></div>
                  <div className="h-1 w-8 bg-gray-700 rounded-full"></div>
                  <div className="h-1 w-8 bg-gray-700 rounded-full"></div>
                </div>
              </div>

              <form onSubmit={handleModalSubmit}>
                <div className="mb-6">
                  <label htmlFor="email" className="block text-gray-300 mb-2 text-sm font-medium">
                    {CONSTANTS.REGISTRATION_MODAL.EMAIL_LABEL}
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white shadow-inner focus:border-purple-500 focus:outline-none"
                    placeholder={CONSTANTS.REGISTRATION_MODAL.EMAIL_PLACEHOLDER}
                  />
                  {!isValid && email && (
                    <p className="text-red-500 text-sm mt-1">Please enter a valid email address</p>
                  )}
                  <p className="text-gray-400 text-xs mt-2">
                    {CONSTANTS.REGISTRATION_MODAL.EMAIL_HELP_TEXT}
                  </p>
                </div>

                {/* Benefits of Registration */}
                <div className="bg-purple-900/10 rounded-lg p-3 mb-6 border border-purple-500/20">
                  <p className="text-purple-300 text-sm font-medium mb-2">{CONSTANTS.REGISTRATION_MODAL.BENEFITS_TITLE}</p>
                  <ul className="text-gray-300 text-xs space-y-1">
                    {CONSTANTS.REGISTRATION_MODAL.BENEFITS.map((benefit, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className="w-3 h-3 text-purple-400 mt-0.5 mr-1 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <motion.button
                  type="submit"
                  className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg text-white font-medium hover:from-purple-600 hover:to-pink-700 transition-all duration-300 flex items-center justify-center shadow-lg shadow-purple-500/20"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {CONSTANTS.REGISTRATION_MODAL.BUTTON_TEXT}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </motion.button>

                <p className="text-gray-400 text-xs text-center mt-4">
                  {CONSTANTS.REGISTRATION_MODAL.PRIVACY_TEXT}
                </p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default LandingPage;
